// DatePickerField — shadcn/ui canonical date-picker pattern: Popover + Calendar +
// Button trigger, value formatted via date-fns. Supports two modes:
//   mode="day"   — picks a full date, emits "YYYY-MM-DD"
//   mode="month" — picks month + year via dropdown caption, emits "YYYY-MM"
// https://ui.shadcn.com/docs/components/radix/date-picker

import * as React from 'react'
import { format, parse, isValid } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface DatePickerFieldProps {
  id?: string
  mode?: 'day' | 'month'
  value: string
  onChange: (next: string) => void
  placeholder?: string
  minYear?: number
  maxYear?: number
}

export function DatePickerField({
  id,
  mode = 'day',
  value,
  onChange,
  placeholder,
  minYear,
  maxYear,
}: DatePickerFieldProps) {
  const parseFormat = mode === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd'
  const display = mode === 'month' ? 'MMMM yyyy' : 'd MMMM yyyy'

  const parsed = value ? parse(value, parseFormat, new Date()) : null
  const selected = parsed && isValid(parsed) ? parsed : undefined

  const [open, setOpen] = React.useState(false)

  const now = new Date()
  const startYear = minYear ?? now.getFullYear() - 30
  const endYear = maxYear ?? now.getFullYear() + 1
  const startMonth = new Date(startYear, 0, 1)
  const endMonth = new Date(endYear, 11, 31)

  function handleSelect(date: Date | undefined) {
    if (!date) return
    if (mode === 'month') {
      // Normalise to day 1 so the emitted string is always YYYY-MM.
      const first = new Date(date.getFullYear(), date.getMonth(), 1)
      onChange(format(first, 'yyyy-MM'))
    } else {
      onChange(format(date, 'yyyy-MM-dd'))
      setOpen(false)
    }
  }

  const label = selected
    ? format(selected, display)
    : placeholder ?? (mode === 'month' ? 'Month and year' : 'Pick a date')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            'w-[260px] justify-start text-left font-serif text-lg h-11',
            !selected && 'text-muted-foreground',
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
          defaultMonth={selected ?? new Date()}
        />
      </PopoverContent>
    </Popover>
  )
}

export default DatePickerField
